// @mui material components
import { Card, Collapse, Fade, Grid, Icon, Modal, Skeleton, Tooltip } from "@mui/material";
import PropTypes from "prop-types";

// Kaizen Dashboard components
import { gql, useLazyQuery } from "@apollo/client";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import dayjs from "dayjs";
import ModalInfraccion from "layouts/infracciones/ModalInfraccion";
import { useEffect, useState } from "react";
import { getColor } from "utils/colors";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "90%", xxl: "70%" },
  overflow: "auto",
};

export default function ModalInfraccionesSolicitante({ open, handleClose, idSolicitante }) {
  const [expanded, setExpanded] = useState(["Historial de Infracciones"]);
  const [openModalPermiso, setOpenModalPermiso] = useState(false);
  const [selectedInfraccion, setSelectedInfraccion] = useState(null);

  const [getPermisos, { data: infracciones, loading: loadingPermisos, refetch: refetchPermisos }] =
    useLazyQuery(gql`
      query getPermisos($filter: JSON) {
        infracciones(filter: $filter) {
          id
          categoria
          fecha
          nroExpediente
          nroResolucion
          motivo
          monto
          localidad {
            nombre
          }
          sancion
          tipoInfraccion
          createdAt
        }
      }
    `);

  const [getUsuario, { data: usuario, loading: loadingUsuario }] = useLazyQuery(gql`
    query getUsuario($id: ID!) {
      usuario(id: $id) {
        id
        nombre
        apellido
        dni
      }
    }
  `);

  useEffect(() => {
    if (idSolicitante && open) {
      getPermisos({ variables: { filter: { idUser: idSolicitante } } });
      getUsuario({ variables: { id: idSolicitante } });
    }
  }, [idSolicitante, open]);

  return (
    <>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={open}
        onClose={handleClose}
        closeAfterTransition
      >
        <Fade in={open}>
          <Card sx={style}>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" p={3} pb={0}>
              <SoftTypography variant="h6">
                {loadingPermisos ? (
                  <Skeleton variant="text" width={200} />
                ) : (
                  `Infracciones de ${usuario?.usuario?.nombre} ${usuario?.usuario?.apellido}`
                )}
              </SoftTypography>
              <SoftBox display="flex" justifyContent="end" alignItems="center">
                <SoftBox display="flex" justifyContent="end" alignItems="center">
                  <Tooltip title="Cerrar" placement="top">
                    <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                      close
                    </Icon>
                  </Tooltip>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <SoftBox p={3}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Card>
                    <SoftBox p={2}>
                      <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                        <SoftBox display="flex" alignItems="center">
                          <SoftBox mr={1} display="flex" alignItems="center">
                            <SoftBox
                              display="grid"
                              justifyContent="center"
                              alignItems="center"
                              bgColor="primary"
                              color="white"
                              width="3rem"
                              height="3rem"
                              shadow="md"
                              borderRadius="lg"
                              variant="gradient"
                            >
                              <Icon fontSize="default">report_gmailerrorred</Icon>
                            </SoftBox>
                          </SoftBox>
                          <SoftBox
                            display="flex-row"
                            alignItems="center"
                            sx={{
                              lineHeight: 0,
                            }}
                          >
                            <SoftTypography variant="h6" fontWeight="bold">
                              Infracciones
                            </SoftTypography>
                            <SoftTypography variant="caption" fontWeight="regular">
                              {infracciones?.infracciones?.length
                                ? `${infracciones?.infracciones?.length} infracciones`
                                : "No hay infracciones"}
                            </SoftTypography>
                          </SoftBox>
                        </SoftBox>
                      </SoftBox>
                      <SoftBox mt={2}>
                        <Card
                          sx={{
                            border: "1px solid rgba(0, 0, 0, 0.12)",
                          }}
                        >
                          <SoftBox
                            display="flex"
                            justifyContent="space-between"
                            px={2}
                            py={1}
                            sx={{
                              borderBottom: !expanded.includes("Historial de Infracciones")
                                ? "none"
                                : "1px solid rgba(0, 0, 0, 0.12)",
                              cursor: "pointer",
                              "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.04)",
                              },
                            }}
                            onClick={(e) => {
                              setExpanded(
                                expanded.includes("Historial de Infracciones")
                                  ? expanded.filter((i) => i !== "Historial de Infracciones")
                                  : [...expanded, "Historial de Infracciones"]
                              );
                            }}
                          >
                            <SoftBox display="flex" alignItems="center">
                              <SoftBox mr={1} display="flex" alignItems="center">
                                <Icon
                                  sx={{
                                    color: getColor("gobSecondary"),
                                  }}
                                >
                                  history
                                </Icon>
                              </SoftBox>
                              <SoftTypography variant="h6" fontWeight="bold">
                                Historial de infracciones
                              </SoftTypography>
                            </SoftBox>
                            <SoftBox display="flex" justifyContent="flex-end" alignItems="center">
                              <SoftBox>
                                <Tooltip
                                  title={
                                    expanded.includes("Historial de Infracciones")
                                      ? "Ocultar"
                                      : "Mostrar"
                                  }
                                  placement="top"
                                >
                                  <SoftButton
                                    color="primary"
                                    iconOnly
                                    onClick={() =>
                                      setExpanded(
                                        expanded.includes("Historial de Infracciones")
                                          ? expanded.filter(
                                              (i) => i !== "Historial de Infracciones"
                                            )
                                          : [...expanded, "Historial de Infracciones"]
                                      )
                                    }
                                  >
                                    <Icon
                                      sx={{
                                        rotate: expanded.includes("Historial de Infracciones")
                                          ? "180deg"
                                          : "0deg",
                                        transition: "all 0.5s",
                                      }}
                                    >
                                      expand_more
                                    </Icon>
                                  </SoftButton>
                                </Tooltip>
                              </SoftBox>
                            </SoftBox>
                          </SoftBox>
                          <Collapse in={expanded.includes("Historial de Infracciones")}>
                            {loadingUsuario || loadingPermisos ? (
                              <Loading />
                            ) : infracciones?.infracciones?.length ? (
                              infracciones?.infracciones?.map((infraccion, index) => (
                                <SoftBox key={infraccion.id}>
                                  <SoftBox
                                    px={2}
                                    py={1}
                                    sx={{
                                      borderBottom:
                                        index === infracciones?.infracciones?.length - 1
                                          ? "none"
                                          : "1px solid rgba(0, 0, 0, 0.12)",
                                      cursor: "pointer",
                                      "&:hover": {
                                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                                      },
                                    }}
                                  >
                                    <SoftBox display="flex" justifyContent="space-between">
                                      <SoftBox
                                        display="flex"
                                        alignItems="center"
                                        pl={{
                                          xs: 0,
                                          sm: 2,
                                        }}
                                      >
                                        <SoftBox
                                          mr={1}
                                          sx={{
                                            display: {
                                              xs: "none",
                                              sm: "block",
                                            },
                                          }}
                                        >
                                          <Icon
                                            sx={{
                                              color: getColor("gobPrimary"),
                                            }}
                                          >
                                            subdirectory_arrow_right
                                          </Icon>
                                        </SoftBox>
                                        <SoftBox
                                          mr={1}
                                          sx={{
                                            display: {
                                              xs: "none",
                                              sm: "flex",
                                            },
                                          }}
                                        >
                                          <Tooltip title={infraccion?.categoria} placement="top">
                                            <Icon
                                              sx={{
                                                color: getColor(
                                                  infraccion?.categoria === "Bosque"
                                                    ? "gobPrimary"
                                                    : "gobSecondary"
                                                ),
                                              }}
                                            >
                                              {infraccion?.categoria === "Bosque" ? "park" : "pets"}
                                            </Icon>
                                          </Tooltip>
                                        </SoftBox>
                                        <SoftBox display="flex" flexDirection="column">
                                          <SoftTypography variant="h6">
                                            {`${infraccion?.tipoInfraccion?.join(", ")}`}
                                            &nbsp;&nbsp;
                                            <SoftBadge
                                              color={"gobSecondary"}
                                              badgeContent={`EXP ${infraccion?.nroExpediente} / RES ${infraccion?.nroResolucion}`}
                                            />
                                          </SoftTypography>
                                          <SoftTypography variant="caption" fontWeight="regular">
                                            {`El día ${dayjs(
                                              infraccion?.fecha || infraccion?.createdAt
                                            ).format("DD/MM/YYYY")} en ${
                                              infraccion?.localidad?.nombre
                                            }`}
                                          </SoftTypography>
                                        </SoftBox>
                                      </SoftBox>
                                      <SoftBox
                                        display="flex"
                                        justifyContent="flex-end"
                                        alignItems="center"
                                      >
                                        <SoftBox>
                                          <Tooltip title={"Ver detalles"} placement="top">
                                            <SoftButton
                                              color={"gobSecondary"}
                                              onClick={() => {
                                                setSelectedInfraccion(infraccion);
                                                setOpenModalPermiso(true);
                                              }}
                                              sx={{
                                                display: {
                                                  xs: "none",
                                                  sm: "flex",
                                                },
                                              }}
                                            >
                                              Ver detalles&nbsp;
                                              <Icon>arrow_forward</Icon>
                                            </SoftButton>
                                          </Tooltip>
                                          <Tooltip title={"Ver detalles"} placement="top">
                                            <SoftButton
                                              color={"gobSecondary"}
                                              iconOnly
                                              onClick={() => {
                                                setSelectedInfraccion(infraccion);
                                                setOpenModalPermiso(true);
                                              }}
                                              sx={{
                                                display: {
                                                  xs: "flex",
                                                  sm: "none",
                                                },
                                              }}
                                            >
                                              <Icon>arrow_forward</Icon>
                                            </SoftButton>
                                          </Tooltip>
                                        </SoftBox>
                                      </SoftBox>
                                    </SoftBox>
                                  </SoftBox>
                                </SoftBox>
                              ))
                            ) : (
                              <SinDatos />
                            )}
                          </Collapse>
                        </Card>
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Grid>
              </Grid>
            </SoftBox>
            <ModalInfraccion
              open={openModalPermiso}
              handleClose={() => setOpenModalPermiso(false)}
              idInfraccion={selectedInfraccion?.id}
              refetch={refetchPermisos}
            />
          </Card>
        </Fade>
      </Modal>
    </>
  );
}

ModalInfraccionesSolicitante.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  idSolicitante: PropTypes.string,
  readOnly: PropTypes.bool,
};
